import React, { useState, useEffect, useRef, useContext } from "react";
import { useParams, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { AuthContext } from "../contexts/AuthContext.js";
import VideoThumb from "../components/VideoThumb.js";
import "./styles/Article.css";

const TrimmedVideo = ({ article, autoPlay }) => {
  const videoRef = useRef(null);
  const startTime = article.video_start_time ?? 0;
  const endTime = article.video_end_time ?? null;

  const handleLoaded = () => {
    if (videoRef.current && startTime > 0) {
      videoRef.current.currentTime = startTime;
    }
  };

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !endTime) return;
    const checkEnd = () => {
      if (video.currentTime >= endTime) {
        video.pause();
        video.currentTime = startTime;
      }
    };
    video.addEventListener("timeupdate", checkEnd);
    return () => video.removeEventListener("timeupdate", checkEnd);
  }, [startTime, endTime]);

  const mimeType =
    article.image_url.endsWith(".webm") ? "video/webm" :
    article.image_url.endsWith(".mov") ? "video/quicktime" :
    "video/mp4";

  return (
    <video
      ref={videoRef}
      className="article-image article-video"
      controls
      playsInline
      preload="metadata"
      autoPlay={autoPlay}
      onLoadedMetadata={handleLoaded}
    >
      <source src={article.image_url} type={mimeType} />
      Your browser does not support video playback.
    </video>
  );
};

const VideoWithCover = ({ article }) => {
  const [playing, setPlaying] = useState(false);
  if (playing) return <TrimmedVideo article={article} autoPlay />;
  if (article.cover_image_url) {
    return (
      <div className="video-cover-wrapper" onClick={() => setPlaying(true)}>
        <img className="article-image" src={article.cover_image_url} alt={article.title} />
        <div className="video-cover-overlay">
          <div className="video-cover-play-btn">&#9654;</div>
        </div>
        <span className="video-cover-badge">&#9654; VIDEO</span>
      </div>
    );
  }
  return (
    <VideoThumb
      src={article.image_url}
      startTime={article.video_start_time}
      alt={article.title}
      imgClassName="article-image"
      onClick={() => setPlaying(true)}
      showBadge
    />
  );
};

const Article = () => {
  const { id } = useParams();
  const { user, token } = useContext(AuthContext);
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const shareRef = useRef(null);

  const SHARE_LABEL = "vyralnews.com";
  const shareUrl = `https://vyralnews.com/article/${encodeURIComponent(id)}`;

  // Close the share menu when clicking outside it or pressing Escape.
  useEffect(() => {
    if (!shareOpen) return undefined;
    const onClick = (e) => {
      if (shareRef.current && !shareRef.current.contains(e.target)) setShareOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setShareOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [shareOpen]);

  const flagCopied = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const shareToFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      "_blank",
      "noopener,noreferrer,width=600,height=600"
    );
    setShareOpen(false);
  };

  const shareToX = () => {
    const text = article?.title || "VyralNews";
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(text)}`,
      "_blank",
      "noopener,noreferrer,width=600,height=600"
    );
    setShareOpen(false);
  };

  // Instagram has no web link-share endpoint, so copy the link and open Instagram
  // for the user to paste into a post, story, or DM. Open the window synchronously
  // (before the async clipboard write) so popup blockers don't intercept it.
  const shareToInstagram = () => {
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
    copyToClipboard(shareUrl);
    setShareOpen(false);
  };

  const handleCopyLink = async () => {
    await copyToClipboard(shareUrl);
    setShareOpen(false);
  };

  // Copies a masked link: rich-text targets show "vyralnews.com" linked to the
  // article; plain-text targets get the URL (which unfurls to a vyralnews.com card).
  const copyToClipboard = async (url) => {
    const linkHtml = `<a href="${url}">${SHARE_LABEL}</a>`;
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([linkHtml], { type: "text/html" }),
            "text/plain": new Blob([url], { type: "text/plain" }),
          }),
        ]);
        flagCopied();
        return;
      }
    } catch (err) {
      // Fall through to the plain-text fallback below.
    }
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(url);
        flagCopied();
      } catch (err) {
        // Clipboard unavailable; nothing more we can do.
      }
    }
  };

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const headers = { "Content-Type": "application/json" };
        if (token) headers.Authorization = `Bearer ${token}`;
        const response = await fetch(`/api/articles/${id}`, { headers });
        if (!response.ok) {
          throw new Error(`Failed to fetch article: ${response.status}`);
        }
        const data = await response.json();
        setArticle(data);
      } catch (err) {
        setError(err.message);
      }
    };

    fetchArticle();
  }, [id, token]);

  if (error) {
    return <p className="error">Error: {error}</p>;
  }

  if (!article) {
    return <p>Loading article...</p>;
  }

  const categoryLabel = article.type ? article.type.charAt(0).toUpperCase() + article.type.slice(1) : "";
  const freemiumInfo = article.freemium_info;
  const galleryItems = Array.isArray(article.media)
    ? article.media.filter((m) => m && m.url && m.url !== article.image_url)
    : [];

  const renderContent = (content) => {
    if (!content) return null;
    // Pasted content (e.g. from Word/Docs) often uses non-breaking spaces between
    // every word, which prevents line wrapping and overflows the layout. Convert
    // them back to regular spaces so text wraps normally.
    const normalized = content.replace(/&nbsp;|\u00a0/g, " ");
    const isHtml = /<\/?[a-z][\s\S]*>/i.test(normalized);
    if (isHtml) {
      const clean = DOMPurify.sanitize(normalized, {
        ADD_ATTR: ["target", "rel"],
      });
      return (
        <div
          className="rich-content"
          dangerouslySetInnerHTML={{ __html: clean }}
        />
      );
    }
    return normalized.split(/\n\n+/).map((paragraph, index) => (
      <p key={index}>{paragraph}</p>
    ));
  };

  return (
    <div className="article-container">
      {user && ["admin", "journalist", "reporter"].includes(user.role) && (
        <div className="article-staff-bar">
          <Link to={`/portal/articles?edit=${article.id}`} className="article-edit-btn">
            ✏ Edit Article
          </Link>
        </div>
      )}
      <div className="article-meta-top">
        {article.is_premium && (
          <span className="article-premium-tag">PREMIUM</span>
        )}
        {article.type && (
          <Link to={`/category/${article.type}`} className="article-category-tag">
            {categoryLabel}
          </Link>
        )}
        {article.subcategory_label && (
          <Link
            to={`/category/${article.type}/${article.subcategory_slug}`}
            className="article-subcategory-tag"
          >
            {article.subcategory_label}
          </Link>
        )}
        {article.region_name && (
          <Link
            to={`/category/local/${article.region_slug}`}
            className="article-region-tag"
          >
            {article.region_name}, {article.region_state}
          </Link>
        )}
      </div>
      <h1>{article.title}</h1>
      {article.excerpt && (
        <p className="article-excerpt">{article.excerpt}</p>
      )}
      <div className="author-meta-row">
        <p className="author-meta">
          By {article.author_name} · {new Date(article.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
        </p>
        <div className="share-wrap" ref={shareRef}>
          <button
            className="share-btn"
            onClick={() => setShareOpen((o) => !o)}
            title="Share article"
            aria-haspopup="true"
            aria-expanded={shareOpen}
          >
            {copied ? "✓ Copied!" : "⎘ Share"}
          </button>
          {shareOpen && (
            <div className="share-menu" role="menu">
              <button type="button" className="share-menu-item" role="menuitem" onClick={handleCopyLink}>
                <span className="share-menu-icon">🔗</span> Copy link
              </button>
              <button type="button" className="share-menu-item" role="menuitem" onClick={shareToFacebook}>
                <span className="share-menu-icon share-fb">f</span> Facebook
              </button>
              <button type="button" className="share-menu-item" role="menuitem" onClick={shareToX}>
                <span className="share-menu-icon share-x">𝕏</span> X
              </button>
              <button type="button" className="share-menu-item" role="menuitem" onClick={shareToInstagram}>
                <span className="share-menu-icon share-ig">⊞</span> Instagram
              </button>
            </div>
          )}
        </div>
      </div>
      {article.image_url && (
        article.media_type === 'video' ? (
          <VideoWithCover article={article} />
        ) : (
          <img className="article-image" src={article.image_url} alt={article.title} />
        )
      )}

      {article.is_premium_gated ? (
        <div className="article-premium-section">
          {article.content && (
            <div className="article-content article-snippet">
              {renderContent(article.content)}
            </div>
          )}
          <div className="article-paywall premium-paywall">
            <div className="paywall-overlay"></div>
            <div className="paywall-content">
              <div className="premium-lock-icon">&#128274;</div>
              <h3>This is a Premium Article</h3>
              <p>Subscribe to VyralNews to unlock this article and get unlimited access to all premium content.</p>
              {!user ? (
                <div className="paywall-actions">
                  <Link to="/signup" className="paywall-btn paywall-btn-primary">Sign Up</Link>
                  <Link to="/login" className="paywall-btn paywall-btn-secondary">Sign In</Link>
                </div>
              ) : (
                <div className="paywall-actions">
                  <Link to="/subscribe" className="paywall-btn paywall-btn-primary">Subscribe Now</Link>
                  <Link to="/pricing" className="paywall-btn paywall-btn-secondary">View Plans</Link>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : article.is_gated ? (
        <div className="article-paywall">
          <div className="paywall-overlay"></div>
          <div className="paywall-content">
            <h3>You've used all {freemiumInfo?.limit || 10} free articles this week</h3>
            <p>Your free articles reset every week. Subscribe for unlimited access to all VyralNews content.</p>
            {!user ? (
              <div className="paywall-actions">
                <Link to="/signup" className="paywall-btn paywall-btn-primary">Sign Up</Link>
                <Link to="/login" className="paywall-btn paywall-btn-secondary">Sign In</Link>
              </div>
            ) : (
              <div className="paywall-actions">
                <Link to="/subscribe" className="paywall-btn paywall-btn-primary">Subscribe Now</Link>
                <Link to="/pricing" className="paywall-btn paywall-btn-secondary">View Plans</Link>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
          {freemiumInfo && (
            <div className="freemium-banner">
              <div className="freemium-banner-text">
                <span className="freemium-count">{freemiumInfo.remaining}</span> of {freemiumInfo.limit} free articles remaining this week
              </div>
              <Link to="/subscribe" className="freemium-banner-cta">Subscribe for unlimited access</Link>
            </div>
          )}
          <div className="article-content">
            {renderContent(article.content)}
          </div>
          {galleryItems.length > 0 && (
            <div className="article-gallery">
              <h3 className="article-gallery-title">Gallery</h3>
              <div className="article-gallery-grid">
                {galleryItems.map((item, i) => (
                  <button
                    key={i}
                    type="button"
                    className="gallery-item"
                    onClick={() => setLightbox(item)}
                    aria-label="View media"
                  >
                    {item.type === "video" ? (
                      <>
                        <video className="gallery-thumb" src={item.url} muted preload="metadata" playsInline />
                        <span className="gallery-play-badge">&#9654;</span>
                      </>
                    ) : (
                      <img className="gallery-thumb" src={item.url} alt={`Gallery item ${i + 1}`} loading="lazy" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
          {freemiumInfo ? (
            <div className="freemium-footer-prompt">
              <h3>Enjoying VyralNews?</h3>
              <p>You have {freemiumInfo.remaining} free {freemiumInfo.remaining === 1 ? "article" : "articles"} left this week. Subscribe to read unlimited articles and support quality journalism.</p>
              <div className="paywall-actions">
                <Link to="/subscribe" className="paywall-btn paywall-btn-primary">Subscribe Now</Link>
                <Link to="/pricing" className="paywall-btn paywall-btn-secondary">See Plans</Link>
              </div>
            </div>
          ) : !user && (
            <div className="freemium-footer-prompt">
              <h3>Enjoying VyralNews?</h3>
              <p>Create a free account to track your reading and get 10 free articles per week. Or subscribe starting at $1.99/month for unlimited access.</p>
              <div className="paywall-actions">
                <Link to="/signup" className="paywall-btn paywall-btn-primary">Create Free Account</Link>
                <Link to="/pricing" className="paywall-btn paywall-btn-secondary">See Plans</Link>
              </div>
            </div>
          )}
        </>
      )}

      {lightbox && (
        <div className="gallery-lightbox" onClick={() => setLightbox(null)}>
          <button
            className="gallery-lightbox-close"
            onClick={() => setLightbox(null)}
            aria-label="Close"
          >
            &times;
          </button>
          <div className="gallery-lightbox-content" onClick={(e) => e.stopPropagation()}>
            {lightbox.type === "video" ? (
              <video className="gallery-lightbox-media" src={lightbox.url} controls autoPlay playsInline />
            ) : (
              <img className="gallery-lightbox-media" src={lightbox.url} alt="Enlarged media" />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Article;
